import React from "react";

import "./DeleteAccountSteps.css";

// React Icons
import { FaCog, FaUserCircle, FaTrash, FaExclamationTriangle, FaCheckCircle, FaEnvelope } from "react-icons/fa";

const APP_NAME = "Corepath";
const DEVELOPER_NAME = "Rain Therapeutics Inc";
const CONTACT_EMAIL = "tanisha@raintherapeutics.co";
const MAIL_SUBJECT = encodeURIComponent(`${APP_NAME} - Account Deletion Request`);
const MAIL_BODY = encodeURIComponent(
  "Hello,\n\nI would like to request deletion of my Corepath account.\n\nRegistered email / phone: \nReason (optional): \n\nThank you."
);

const steps = [
  {
    title: "Go to Settings",
    desc: "Open the app and navigate to the Settings menu from the home screen.",
    icon: <FaCog size={24} color="white" />,
  },
  {
    title: "Select Account",
    desc: "Tap on 'Account' or 'Manage Account' from the Settings options.",
    icon: <FaUserCircle size={24} color="white" />,
  },
  {
    title: "Choose Delete Account",
    desc: "Scroll down and select the 'Delete Account' option.",
    icon: <FaTrash size={24} color="white" />,
  },
  {
    title: "Read the Warning",
    desc: "Understand that this action is irreversible and all data will be removed.",
    icon: <FaExclamationTriangle size={24} color="white" />,
  },
  {
    title: "Confirm & Delete",
    desc: "Authenticate and confirm the deletion to permanently delete your account.",
    icon: <FaCheckCircle size={24} color="white" />,
  },
];

const deletedData = [
  "Account profile (name, email, phone number, date of birth)",
  "Health and treatment records, medication and adherence history",
  "Bladder diary entries, survey responses and learning progress",
  "Chat messages, community posts and uploaded documents",
  "Device tokens and notification preferences",
];

const retainedData = [
  "Records we are legally required to keep (e.g. audit logs, billing or regulatory records) for the period required by law",
  "Anonymised, aggregated data that can no longer be linked to you",
];

const DeleteAccountSteps = () => {
  const mailtoHref = `mailto:${CONTACT_EMAIL}?subject=${MAIL_SUBJECT}&body=${MAIL_BODY}`;

  return (
    <div className="container py-5 d-flex justify-content-center align-items-start min-vh-100 bg-light">
      <div className="card p-4 shadow-lg" style={{ maxWidth: "760px", width: "100%", borderRadius: "1rem" }}>
        <h2 className="mb-2 text-center text-primary fw-bold">Delete Your {APP_NAME} Account</h2>
        <p className="text-center text-muted mb-4">
          This page explains how users of the <strong>{APP_NAME}</strong> mobile app, developed by{" "}
          <strong>{DEVELOPER_NAME}</strong>, can request deletion of their account and associated data.
        </p>

        {/* Option 1: in-app */}
        <h4 className="fw-semibold mb-3">Option 1: Delete from within the app</h4>
        <div className="timeline mb-4">
          {steps.map((step, index) => (
            <div key={index} className="timeline-step d-flex align-items-start mb-4">
              <div className="icon-box me-3">{step.icon}</div>
              <div>
                <h5 className="fw-semibold">{step.title}</h5>
                <p className="text-muted mb-0">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Option 2: email request */}
        <h4 className="fw-semibold mb-3">Option 2: Request deletion by email</h4>
        <p className="text-muted">
          If you no longer have access to the app, you can request account deletion without logging in. Send an
          email from your registered email address to{" "}
          <a href={mailtoHref}>{CONTACT_EMAIL}</a> with the subject{" "}
          <em>"{APP_NAME} - Account Deletion Request"</em> and include the email address or phone number linked to
          your account so we can verify your identity.
        </p>
        <div className="d-grid mb-4">
          <a href={mailtoHref} className="btn btn-primary btn-lg d-flex align-items-center justify-content-center gap-2">
            <FaEnvelope /> Email a Deletion Request
          </a>
        </div>

        {/* What gets deleted */}
        <h4 className="fw-semibold mb-3">What data will be deleted</h4>
        <ul className="text-muted mb-4">
          {deletedData.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>

        <h4 className="fw-semibold mb-3">What data may be retained</h4>
        <ul className="text-muted mb-4">
          {retainedData.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>

        {/* Timeline */}
        <h4 className="fw-semibold mb-3">Processing time</h4>
        <p className="text-muted mb-0">
          Deletion requests are processed within <strong>30 days</strong> of receipt. Once processed, your
          account cannot be recovered. If you have any questions, contact us at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </div>
    </div>
  );
};

export default DeleteAccountSteps;
